"use client";

import { BrandLockup } from "@/components/brand/BrandLockup";
import {
  type LanguageLevel,
  PRACTICE_START_LABEL,
  PracticeLevelSelector,
  getPracticeLevelStartLabel,
} from "@/components/fullscreen/PracticeLevelSelector";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Mic, Play } from "lucide-react";
import { type CSSProperties, Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FirstRunChicky } from "./FirstRunChicky";
import styles from "./FirstRunIntro.module.css";
import { FirstRunSun } from "./FirstRunSun";
import {
  FIRST_RUN_LANGUAGES,
  GREETING_CYCLE,
  compactLanguageName,
  firstRunLanguage,
  languageName,
  nativeLanguageName,
} from "./first-run-languages";
import { firstRunSerif } from "./fonts";

const GREETING_MS = 1800;
const EASE = [0.22, 1, 0.36, 1] as const;

const LEAD_LINES = ["I'm Chicky, your AI language tutor.", "Which language shall we speak?"];
/** Chicky's question streams in like a reply: a beat after arrival, word by word. */
const STREAM_START_MS = 350;
const STREAM_WORD_MS = 75;
const STREAM_PAUSE_MS = 280;

export interface FirstRunIntroProps {
  /** Every practice language, name to code, for More languages. */
  languages: Record<string, string>;
  selectedLanguageCode: string | null;
  level: LanguageLevel;
  /** The language Chicky explains in, as today's "Instruction language". */
  instructionLanguageCode: string;
  /** Start speaking was pressed and the session is being prepared. */
  starting?: boolean;
  /** The session is ready behind the intro: the horizon sets and reveals it. */
  leaving?: boolean;
  onSelectLanguage: (code: string) => void;
  onLevelChange: (level: LanguageLevel) => void;
  onInstructionLanguageChange: (code: string) => void;
  onStart: () => void;
  onSignIn?: () => void;
  /** The quiet way to look before speaking: today's "See how ChickyTutor works". */
  onHowItWorks?: () => void;
  /** The full language list was opened from the first step. */
  onMoreLanguages?: () => void;
  /** The real intro is mounted, so its assigned arm may be counted as exposed. */
  onShown?: () => void;
}

type Phase = "choose" | "ready";

/** One line of Chicky's voice, set smaller when it is wider than the screen. */
function FitText({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const line = ref.current;
    const box = line?.closest<HTMLElement>("[data-voice]");
    if (!line || !box) return;
    const fit = () => {
      // Its width at full size, whatever size it has now.
      const current = Number(line.style.getPropertyValue("--fit")) || 1;
      const natural = line.offsetWidth / current;
      const scale = natural ? Math.min(1, box.clientWidth / natural) : 1;
      line.style.setProperty("--fit", String(scale));
    };
    fit();
    // The serif face can arrive after the first paint and change the width.
    void document.fonts?.ready.then(fit);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [text]);
  return (
    <span ref={ref} className={styles.fit}>
      {text}
    </span>
  );
}

/**
 * Chicky's question, streamed in word by word with a caret, like a reply being
 * written. The whole question is laid out from the first frame (unstreamed
 * words are only transparent), so nothing moves, the languages below are
 * usable at once, and assistive technology reads it whole.
 */
function StreamedLead({ reduce, onComplete }: { reduce: boolean; onComplete?: () => void }) {
  const lines = LEAD_LINES.map((line) => line.split(" "));
  const total = lines.reduce((count, line) => count + line.length, 0);
  const firstLineLength = lines[0].length;
  const [shown, setShown] = useState(reduce ? total : 0);

  useEffect(() => {
    if (reduce) {
      setShown(total);
      return;
    }
    const timers: number[] = [];
    let at = STREAM_START_MS;
    for (let count = 1; count <= total; count += 1) {
      timers.push(window.setTimeout(() => setShown(count), at));
      at += STREAM_WORD_MS + (count === firstLineLength ? STREAM_PAUSE_MS : 0);
    }
    timers.push(window.setTimeout(() => onComplete?.(), at));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // onComplete is read when the question completes; a new callback doesn't restart it.
  }, [firstLineLength, reduce, total]);

  let index = 0;
  return (
    <h1 className={styles.lead}>
      <span className={styles.srOnly}>{LEAD_LINES.join(" ")}</span>
      <span aria-hidden="true" data-streaming={shown < total || undefined}>
        {lines.map((line, lineIndex) => (
          <span key={lineIndex} className={styles.leadLine}>
            {line.map((word, wordIndex) => {
              const position = index++;
              return (
                <Fragment key={wordIndex}>
                  {wordIndex > 0 && " "}
                  <span className={styles.word} data-shown={position < shown || undefined}>
                    {word}
                    {!reduce && position === shown - 1 && <span className={styles.caret} />}
                  </span>
                </Fragment>
              );
            })}
          </span>
        ))}
      </span>
    </h1>
  );
}

function enter(reduce: boolean, delay: number) {
  if (reduce) return { initial: false as const };
  return {
    initial: { opacity: 0, y: 12, filter: "blur(6px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    transition: { duration: 0.55, delay, ease: EASE },
  };
}

interface ChoosePanelProps {
  languages: Record<string, string>;
  selectedLanguageCode: string | null;
  reduce: boolean;
  onPoint: (code: string | null) => void;
  onChoose: (code: string) => void;
  onMoreLanguages?: () => void;
}

function ChoosePanel({
  languages,
  selectedLanguageCode,
  reduce,
  onPoint,
  onChoose,
  onMoreLanguages,
}: ChoosePanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  // The languages are usable from the first frame. Once the question has
  // finished, a soft glow passes over them once, in reading order: your turn.
  const [invited, setInvited] = useState(false);
  const order = (index: number) => ({ "--i": index }) as CSSProperties;

  return (
    <motion.div
      className={styles.choose}
      exit={
        reduce
          ? undefined
          : { opacity: 0, y: 8, filter: "blur(8px)", transition: { duration: 0.28, ease: EASE } }
      }
    >
      <StreamedLead reduce={reduce} onComplete={() => setInvited(true)} />

      <ul className={styles.choices} data-invited={invited || undefined}>
        {FIRST_RUN_LANGUAGES.map((language, index) => (
          <li key={language.code}>
            <button
              type="button"
              className={styles.choice}
              style={order(index)}
              data-selected={language.code === selectedLanguageCode || undefined}
              aria-label={`Choose ${language.name}`}
              onPointerEnter={() => onPoint(language.code)}
              onPointerLeave={() => onPoint(null)}
              onFocus={() => onPoint(language.code)}
              onBlur={() => onPoint(null)}
              onClick={() => onChoose(language.code)}
            >
              {language.name}
            </button>
          </li>
        ))}
        <li>
          <ResponsiveLanguagePicker
            open={pickerOpen}
            onOpenChange={(open) => {
              setPickerOpen(open);
              if (open) onMoreLanguages?.();
            }}
            trigger={
              <button
                type="button"
                className={`${styles.choice} ${styles.more}`}
                style={order(FIRST_RUN_LANGUAGES.length)}
              >
                More languages <span className={styles.moreCount}>70+</span>
              </button>
            }
            languages={languages}
            selectedLanguageCode={selectedLanguageCode ?? ""}
            onSelectLanguage={(code) => {
              setPickerOpen(false);
              onChoose(code);
            }}
            searchTerm={search}
            onSearchTermChange={setSearch}
            title="Choose a practice language"
            desktopAlign="center"
          />
        </li>
      </ul>
    </motion.div>
  );
}

/** Today's "Instruction language", as a quiet inline choice. */
function ExplanationPicker({
  languages,
  code,
  onChange,
}: {
  languages: Record<string, string>;
  code: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const name = compactLanguageName(languages, code);
  return (
    <div className={styles.setting}>
      <span className={styles.settingLabel}>Explained in</span>
      <ResponsiveLanguagePicker
        open={open}
        onOpenChange={setOpen}
        trigger={
          <button
            type="button"
            className={styles.settingPill}
            aria-label={`Change explanation language, currently ${name}`}
          >
            {name}
            <ChevronDown size={16} aria-hidden="true" />
          </button>
        }
        languages={languages}
        selectedLanguageCode={code}
        onSelectLanguage={(next) => {
          setOpen(false);
          onChange(next);
        }}
        searchTerm={search}
        onSearchTermChange={setSearch}
        title="Choose an instruction language"
        desktopAlign="center"
      />
    </div>
  );
}

interface ReadyPanelProps {
  languages: Record<string, string>;
  name: string | null;
  meaning: string | null;
  level: LanguageLevel;
  instructionLanguageCode: string;
  starting: boolean;
  focusStart: boolean;
  reduce: boolean;
  onLevelChange: (level: LanguageLevel) => void;
  onInstructionLanguageChange: (code: string) => void;
  onStart: () => void;
  selectedLanguageCode: string | null;
  onChangeLanguage: (code: string) => void;
}

function ReadyPanel({
  languages,
  name,
  meaning,
  level,
  instructionLanguageCode,
  starting,
  focusStart,
  reduce,
  onLevelChange,
  onInstructionLanguageChange,
  onStart,
  selectedLanguageCode,
  onChangeLanguage,
}: ReadyPanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const startRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusStart) startRef.current?.focus({ preventScroll: true });
  }, [focusStart]);
  const preparing = `Preparing ${name ?? "your"} practice`;

  return (
    <motion.div
      className={styles.ready}
      exit={reduce ? undefined : { opacity: 0, transition: { duration: 0.2 } }}
    >
      {/* The language itself changes the language, here, without going back. */}
      <motion.p className={styles.gloss} {...enter(reduce, 0.2)}>
        <ResponsiveLanguagePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          trigger={
            <button
              type="button"
              className={styles.languagePill}
              disabled={starting}
              aria-label={`Change language, currently ${name ?? "not chosen"}`}
            >
              {name}
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          }
          languages={languages}
          selectedLanguageCode={selectedLanguageCode ?? ""}
          onSelectLanguage={(code) => {
            setPickerOpen(false);
            onChangeLanguage(code);
          }}
          searchTerm={search}
          onSearchTermChange={setSearch}
          title="Choose a practice language"
          desktopAlign="center"
        />
        {meaning && (
          <span>
            for <span className={styles.meaning}>“{meaning}”</span>
          </span>
        )}
      </motion.p>
      {/* Framer owns the wrappers' opacity; .aside fades the content while starting. */}
      <motion.div className={styles.level} {...enter(reduce, 0.3)}>
        <div className={`${styles.settings} ${styles.aside}`}>
          <PracticeLevelSelector appearance="inline" value={level} onValueChange={onLevelChange} />
          <ExplanationPicker
            languages={languages}
            code={instructionLanguageCode}
            onChange={onInstructionLanguageChange}
          />
        </div>
      </motion.div>
      <motion.div className={styles.startRow} {...enter(reduce, 0.38)}>
        <button
          ref={startRef}
          type="button"
          className={styles.start}
          data-shine={reduce ? undefined : ""}
          onClick={onStart}
          disabled={starting}
          aria-label={starting ? preparing : getPracticeLevelStartLabel(level)}
        >
          <span className={styles.startIcon} data-spinning={starting || undefined}>
            <Mic size={20} aria-hidden="true" />
          </span>
          <span className={styles.startLabel}>
            {starting ? `${preparing}…` : PRACTICE_START_LABEL}
          </span>
        </button>
      </motion.div>
      <motion.div {...enter(reduce, 0.46)}>
        <p className={`${styles.micNote} ${styles.aside}`}>
          Your browser will ask to use your microphone.
        </p>
      </motion.div>
    </motion.div>
  );
}

/**
 * The first ten seconds for a new guest on `/`: Chicky greets them in several
 * languages, they choose one, she invites them to start in it, and `Start
 * speaking` begins the session. It is the setup itself, not a screen before it,
 * and it only calls the handlers the regular setup uses.
 *
 * The sky: a sun, as a solar telescope shows it, rising behind a dark planet.
 */
export function FirstRunIntro({
  languages,
  selectedLanguageCode,
  level,
  instructionLanguageCode,
  starting = false,
  leaving = false,
  onSelectLanguage,
  onLevelChange,
  onInstructionLanguageChange,
  onStart,
  onSignIn,
  onHowItWorks,
  onMoreLanguages,
}: FirstRunIntroProps) {
  const reduce = Boolean(useReducedMotion());
  const [phase, setPhase] = useState<Phase>(selectedLanguageCode ? "ready" : "choose");
  const [chose, setChose] = useState(false);
  const [pointed, setPointed] = useState<string | null>(null);
  const [cycle, setCycle] = useState(0);
  // The decorative detail (the sun's surface, the grain) is drawn after the
  // first paint, so the question and the languages never wait for it.
  const [detailed, setDetailed] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setDetailed(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (reduce || phase !== "choose" || pointed) return;
    const timer = window.setInterval(
      () => setCycle((index) => (index + 1) % GREETING_CYCLE.length),
      GREETING_MS
    );
    return () => window.clearInterval(timer);
  }, [phase, pointed, reduce]);

  const choose = (code: string) => {
    setPointed(null);
    onSelectLanguage(code);
    setChose(true);
    setPhase("ready");
  };

  const ready = phase === "ready" && Boolean(selectedLanguageCode);
  const chosen = firstRunLanguage(selectedLanguageCode);
  const name = languageName(languages, selectedLanguageCode);
  // Without a featured phrase, the language's own name; never the English name twice.
  const native =
    !chosen && selectedLanguageCode ? nativeLanguageName(selectedLanguageCode, name) : null;
  const greeting = firstRunLanguage(pointed ?? GREETING_CYCLE[cycle]) ?? FIRST_RUN_LANGUAGES[0];
  const voice = ready
    ? {
        key: `invite:${selectedLanguageCode}`,
        text: chosen?.invitation ?? native ?? "Ready when you are.",
        lang: chosen?.lang ?? (native ? (selectedLanguageCode ?? "en") : "en"),
      }
    : { key: `hello:${greeting.code}`, text: greeting.hello, lang: greeting.lang };
  const voiceMotion = reduce
    ? { initial: false as const, exit: { opacity: 0, transition: { duration: 0 } } }
    : {
        initial: { opacity: 0, y: 14, filter: "blur(14px)" },
        animate: { opacity: 1, y: 0, filter: "blur(0px)" },
        exit: { opacity: 0, y: -10, filter: "blur(10px)" },
        transition: { duration: ready ? 0.75 : 0.6, ease: EASE },
      };
  return (
    <div
      className={`${styles.scene} ${firstRunSerif.variable}`}
      data-phase={leaving ? "leaving" : starting ? "starting" : ready ? "ready" : "choose"}
      inert={leaving || undefined}
      data-first-run-intro
      data-first-run-detailed={detailed || undefined}
    >
      <div className={styles.sky} aria-hidden="true">
        <div className={styles.dawn} />
        <div className={styles.sunPlace}>
          <FirstRunSun detailed={detailed} />
        </div>
        <div className={styles.planet} />
        <div className={styles.rimFade}>
          <div className={styles.rim} />
        </div>
        <FirstRunChicky />
        <div className={styles.grain} />
      </div>

      <header className={styles.top}>
        <BrandLockup surface="dark" className={styles.lockup} priority />
        {onSignIn && (
          <button type="button" className={`${styles.signIn} ${styles.aside}`} onClick={onSignIn}>
            Sign in
          </button>
        )}
      </header>

      <div className={styles.stage}>
        <div className={styles.voice} data-voice>
          {/* Nothing animates in on the first frame; only later changes do. */}
          <AnimatePresence initial={false}>
            {ready ? (
              <motion.h1
                key={voice.key}
                lang={voice.lang}
                className={styles.voiceLine}
                {...voiceMotion}
              >
                <FitText text={voice.text} />
              </motion.h1>
            ) : (
              <motion.p
                key={voice.key}
                lang={voice.lang}
                aria-hidden="true"
                className={styles.voiceLine}
                {...voiceMotion}
              >
                <FitText text={voice.text} />
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {ready ? (
            <ReadyPanel
              key="ready"
              name={name}
              languages={languages}
              meaning={chosen?.meaning ?? null}
              instructionLanguageCode={instructionLanguageCode}
              onInstructionLanguageChange={onInstructionLanguageChange}
              level={level}
              starting={starting || leaving}
              focusStart={chose}
              reduce={reduce}
              onLevelChange={onLevelChange}
              onStart={onStart}
              selectedLanguageCode={selectedLanguageCode}
              onChangeLanguage={onSelectLanguage}
            />
          ) : (
            <ChoosePanel
              key="choose"
              languages={languages}
              selectedLanguageCode={selectedLanguageCode}
              reduce={reduce}
              onPoint={setPointed}
              onChoose={choose}
              onMoreLanguages={onMoreLanguages}
            />
          )}
        </AnimatePresence>
      </div>

      {onHowItWorks && (
        <button
          type="button"
          className={`${styles.howItWorks} ${styles.aside}`}
          onClick={onHowItWorks}
        >
          <Play size={14} aria-hidden="true" />
          See how ChickyTutor works
        </button>
      )}
    </div>
  );
}
